import { push, replace } from 'connected-react-router';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { Request } from '../../api/request';
import { API_URL, ENDPOINTS } from '../../constants/api';
import { CLAIM_KEPT_ERROR_CODES, ErrorCodeEnum } from '../../constants/errors';
import { CHOOSE_OPTIONS_TO_CONTINUE_TEXT, MARKETPLACE_ENGAGEMENT_ID } from '../../constants/marketplace';
import { ROUTE_NAMES } from '../../constants/routes';
import { SUCCESS_MESSAGE, SUCCESS_TITLE } from '../../constants/ui';
import * as Analytics from '../../hooks/useAnalytics';
import { ICustomizableItem, IPickedMskuItem } from '../../types/api';
import { IResponse } from '../../types/http';
import { ClaimSourceEnum, TMarketplaceSubmitLine } from '../../types/marketplace';
import { ICampaign, ICampaignItem } from '../../types/pyg';
import {
  ActionsEnum,
  IAction,
  IError,
  ISubmitAddressPayload,
  ISubmitMSKUPayload,
  ISubmitPYGPayload,
  TUpdateRequestBody,
} from '../../types/store';
import {
  convertPickedItemsToInventoryItems,
  getCustomizableItemsList,
  isCustomizableItem,
  isCustomizableItemsIncluded,
  isMskuIncluded,
  isMskuItem as isMskuIncludedItem,
} from '../../util/helpers';
import { isMarketplaceId } from '../../util/marketplace';
import { setCustomizableItems, updateFailureAction, updateSuccessAction } from '../actions/form';
import { setIsFormSubmitted } from '../actions/init';
import * as KeysActions from '../actions/keys';
import { marketplaceClearCartAction } from '../actions/marketplace';
import {
  selectCustomizableItemsForUpdateRequest,
  selectPickedCampaignId,
  selectPickedItemsForUpdateRequest,
} from '../selectors/form';
import { selectCampaigns, selectItems } from '../selectors/init';
import { selectInitKeys } from '../selectors/keys';
import {
  selectClaimSource,
  selectHasMissingVariantPicks,
  selectHasUnresolvedMarketplaceItems,
  selectMarketplaceSubmitLines,
  selectMarketplaceSubmitPicks,
  selectNeedsMarketplaceOptionsStepFn,
} from '../selectors/marketplace';
import { prepareAuthHeadersSaga } from './helpers';

export function* updateSaga(action: IAction<ISubmitAddressPayload>) {
  const { values: shippingValues, resolve, reject } = action.payload || {};

  try {
    const { order_id, key, one_link_id } = yield select(selectInitKeys);
    // tslint:disable-next-line:variable-name
    const pre_created_engagement_id = (yield select(selectPickedCampaignId)) as string;
    const pickedItems: { picked_items: IPickedMskuItem[] } | undefined = yield select(
      selectPickedItemsForUpdateRequest,
    );
    const customizedItems: ICustomizableItem[] | null = yield select(selectCustomizableItemsForUpdateRequest);
    const claimSource: ClaimSourceEnum = yield select(selectClaimSource);

    const hasMissingVariantPicks: boolean = yield select(selectHasMissingVariantPicks);

    if (hasMissingVariantPicks) {
      const error: IError = { message: CHOOSE_OPTIONS_TO_CONTINUE_TEXT };

      if (reject) {
        yield call(reject, error);
      }
      yield put(updateFailureAction(error));

      return;
    }

    let endpoint = '';
    const headers: Record<string, string> = yield call(prepareAuthHeadersSaga);

    // Exactly one selection channel per submit: a campaign UID, the "marketplace" sentinel with
    // prefixed picked_items, or the root marketplace list (budget shopping).
    let selectionChannel: TUpdateRequestBody;

    switch (claimSource) {
      case ClaimSourceEnum.MarketplaceBudget: {
        const marketplace: TMarketplaceSubmitLine[] = yield select(selectMarketplaceSubmitLines);
        selectionChannel = { marketplace };
        break;
      }
      case ClaimSourceEnum.MarketplacePreselected: {
        const marketplacePicks: IPickedMskuItem[] = yield select(selectMarketplaceSubmitPicks);
        selectionChannel = { pre_created_engagement_id: MARKETPLACE_ENGAGEMENT_ID, picked_items: marketplacePicks };
        break;
      }
      default: {
        selectionChannel = { pre_created_engagement_id, ...pickedItems };
      }
    }

    const requestBody: TUpdateRequestBody = {
      ...shippingValues,
      ...selectionChannel,
      ...customizedItems,
    };

    if (order_id && key) {
      endpoint = `${API_URL}${ENDPOINTS.updateAddress}`;
      requestBody.order_id = order_id;
      requestBody.key = key;
    } else if (one_link_id) {
      endpoint = `${API_URL}${ENDPOINTS.createOneLinkEngagement}`;
      requestBody.one_link_id = one_link_id;
    } else if (reject) {
      yield call(reject);
      yield call(Analytics.exception, { message: 'Unexpected error - no keys data in update request' });

      return;
    }

    const response: IResponse<null | { error: IError }> = yield call<any>(Request, {
      headers,
      endpoint,
      method: 'POST',
      body: JSON.stringify(requestBody),
    });

    if (response.ok) {
      if (resolve) {
        yield call(resolve, response.body);
      }

      yield put(
        push(ROUTE_NAMES.success, {
          message: SUCCESS_MESSAGE,
          title: SUCCESS_TITLE,
          status: response.status,
        }),
      );
      yield put(updateSuccessAction());
      yield put(setIsFormSubmitted(true));
    } else {
      const { title, message, error_code } = (response.body as IError) || {};
      const isClaimKeptError = response.status === 400 && CLAIM_KEPT_ERROR_CODES.includes(error_code as ErrorCodeEnum);
      if (response.status === 409 || isClaimKeptError) {
        yield put(
          push(ROUTE_NAMES.error, {
            message,
            title,
            status: response.status,
          }),
        );
      } else if (response.status === 401) {
        yield put(replace(ROUTE_NAMES.authentication));
        yield put(KeysActions.updateKeysAction({ allowed_recipient_uid: '' }));
      } else {
        if (reject) {
          yield call(reject, response.body);
        }
      }
      yield put(updateFailureAction(response.body as IError));
      yield call(Analytics.apiException, response.body);
    }
  } catch (e) {
    if (reject) {
      yield call(reject, e);
    }
    yield put(updateFailureAction(e));
    yield call(Analytics.exception, e);
  }
}

export function* submitFlowStepWorkerSaga(
  action: IAction<ISubmitPYGPayload | ISubmitMSKUPayload>,
): Generator<any, any, any> {
  let redirectTo: string = ROUTE_NAMES.address;
  let shouldSetMsku = false;
  let shouldSetCustomItems = false;

  const { resolve } = action.payload || {};

  yield put(setCustomizableItems(null));

  switch (action.type) {
    case ActionsEnum.SubmitPYG: {
      const { campaignId } = (action.payload as ISubmitPYGPayload) || {};
      if (campaignId) {
        const campaignList: ICampaign[] | null = yield select(selectCampaigns);
        const campaign = campaignList?.find(({ id }) => id === campaignId);
        // Details are fetched before Continue enables, so the variant count is known here: a
        // marketplace item with a real choice goes to the options step, a single-variation one
        // has nothing to pick and skips straight to shipping. Same predicate as the button label,
        // so the two cannot disagree.
        const needsOptionsStepOf: (itemId?: string) => boolean = yield select(selectNeedsMarketplaceOptionsStepFn);
        const isMarketplaceCampaignWithOptions = isMarketplaceId(campaignId) && needsOptionsStepOf(campaignId);
        shouldSetMsku = isMarketplaceCampaignWithOptions || (!!campaign && isMskuIncluded(campaign));
        shouldSetCustomItems = !!campaign && isCustomizableItemsIncluded(campaign.items);

        if (shouldSetMsku) {
          redirectTo = ROUTE_NAMES.pickMsku;
          break;
        }

        if (shouldSetCustomItems) {
          const customizableItems = campaign?.items.filter(isCustomizableItem);
          yield put(setCustomizableItems(customizableItems || []));
          redirectTo = ROUTE_NAMES.customizeItems;
        }
      }
      break;
    }
    case ActionsEnum.SubmitKit: {
      yield put(marketplaceClearCartAction());
      const items: ICampaignItem[] | null = yield select(selectItems);
      const needsOptionsStepOf: (itemId?: string) => boolean = yield select(selectNeedsMarketplaceOptionsStepFn);
      const hasUnresolvedMarketplaceItems: boolean = yield select(selectHasUnresolvedMarketplaceItems);

      if (
        hasUnresolvedMarketplaceItems ||
        items?.some((item) => isMskuIncludedItem(item) || needsOptionsStepOf(item.id))
      ) {
        redirectTo = ROUTE_NAMES.pickMsku;
        break;
      }

      const customizableItems = yield call(getCustomizableItemsList, items);

      if (customizableItems.length) {
        yield put(setCustomizableItems(customizableItems));
        redirectTo = ROUTE_NAMES.customizeItems;
      }
      break;
    }
    case ActionsEnum.SubmitMSKU: {
      const { pickedItems } = (action.payload as ISubmitMSKUPayload) || {};
      const inventoryItems: ICampaignItem[] | null = yield select(selectItems);
      const customizableInventoryItems = yield call(getCustomizableItemsList, inventoryItems);
      const pickedItemsList = yield call(convertPickedItemsToInventoryItems, pickedItems, inventoryItems);
      const customizableItems = [...customizableInventoryItems, ...pickedItemsList.filter(isCustomizableItem)];
      const shouldCustomizeItems = customizableItems.length > 0;

      if (shouldCustomizeItems) {
        yield put(setCustomizableItems(customizableItems));
        redirectTo = ROUTE_NAMES.customizeItems;
      }
      break;
    }
    default:
      redirectTo = ROUTE_NAMES.address;
  }

  if (resolve) {
    yield call(resolve, redirectTo);
  }
}

const sagas = {
  *watchUpdateRequest() {
    yield takeLatest(ActionsEnum.UpdateRequest, updateSaga);
  },
  *watchSubmitFlowStep() {
    yield takeLatest([ActionsEnum.SubmitPYG, ActionsEnum.SubmitKit, ActionsEnum.SubmitMSKU], submitFlowStepWorkerSaga);
  },
};

export default sagas;
